/**
 * features/exams/ExamSeatingPane.tsx — rooms, desks, and the plan.
 * ------------------------------------------------------------------
 * Phase 4, the last of the four the school scoped. Two things in one screen
 * because they are useless apart: the ROOMS the school has, and a SESSION's
 * candidates seated in them.
 *
 * A PLAN IS PER SESSION. 25 of the 48 sessions run several papers at once in
 * one room, so a desk belongs to a candidate for the session and carries the
 * papers they sit at it. The biggest session is 81 candidates over three
 * papers; thirteen are ten or fewer.
 *
 * ALLOCATING NEVER MOVES SOMEBODY ALREADY SEATED unless you ask it to. The
 * plan is printed and taped to desks days in advance, so a candidate added on
 * the Friday takes a free desk — "Start again" is a separate, deliberate
 * button.
 *
 * SEATING BY HAND, OR BY PATTERN (2026-09-22). Candidates stand in a list
 * down the left in candidate-number order; drag one onto a desk, or tap the
 * card and tap the desk — the two taps stay, because a drag is unusable on
 * the phone this is opened on in a hall. Once a few desks are filled and the
 * sequence has TURNED at the end of a row or column, the screen recognises
 * the order and offers to finish the room that way (lib/examSeating:
 * detectFillPattern, completePattern).
 *
 * ROOMS ARE CHOSEN, NOT TYPED. The picker lists the four exam rooms first,
 * then every teaching room grouped by block, from `timetableRooms/list`
 * (lib/examRoomCatalogue). Picking one creates its `reportExamRooms` record
 * with a suggested grid; how many desks fit wide and deep is set right there
 * on the room's card.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowDown, ArrowUp, Ban, CheckCircle2, DoorOpen, Eraser, FileDown, Grid3x3, Maximize2,
  Minimize2, Plus, Redo2, RotateCcw, Save, Sparkles, Trash2, Undo2, Users, Wand2, X,
} from 'lucide-react';
import { collection, deleteDoc, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db, setDoc } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import {
  PanelHeader, SoftCard, StatChip, StatStrip, btnGhost, btnPrimary, glassFieldClass,
} from '@/components/ui/design';
import {
  autoSeat, blockKey, deskKey, desksOf, detectFillPattern, moveSeat, roomCapacity, roomPlan,
  seatingId, seatingProblems, sessionCandidates, totalCapacity, unseat, FILL_PATTERN_LABEL,
  SEAT_ORDER_LABEL, SPACING_LABEL,
  type AutoOptions, type ExamRoom, type FillPattern, type Seat, type SeatOrder, type SeatingCandidate,
  type Spacing,
} from '@/lib/examSeating';
import { roomCatalogue, suggestedDesks } from '@/lib/examRoomCatalogue';
import { colourFor, paperColours, type PaperColour } from '@/lib/examPaperColours';
import {
  DEFAULT_FIXTURES, WALLS, WALL_LABEL, defaultInvigilator, doorBlocked, roomLayout,
  type Door, type Wall,
} from '@/lib/examRoomLayout';
import { sessionName, type PupilExamEntries } from '@/lib/examTimetable';
import { shortDay } from './ExamsHub';

interface SeatingDoc {
  id: string;
  seriesId: string;
  date: string;
  session: string;
  roomIds: string[];
  seats: Record<string, Seat>;
  /** The order the office last filled a room in, remembered for next time. */
  fillPattern?: FillPattern;
  /** How close the automatic plan seats people (absent reads 'spread'). */
  spacing?: Spacing;
  /** Which candidates it seats first when papers share the room. */
  order?: SeatOrder;
  /** Desks left empty in this session only, `${roomId}|r2c3`. */
  blocked?: string[];
  /** At most this many candidates in a room, by room id. */
  limits?: Record<string, number>;
  /** The order the exams are seated in, by code ("Paper by paper"). */
  paperOrder?: string[];
  /** Start each exam at the front of a fresh column (or row). */
  freshLinePerPaper?: boolean;
  updatedAt?: string;
}

export default function ExamSeatingPane({ seriesId, seriesTitle, internal = false }: { seriesId: string; seriesTitle: string; internal?: boolean }) {
  /* AM / PM for Cambridge, as the office writes it; Session 1 / 2 for the
     school's own exams, which both run in the morning. */
  const sessionText = (s: string) => (internal ? sessionName(s, true) : s);
  const { permissions, userData } = useAuth();
  const { students } = useData();
  const canEdit = permissions.canManageExamTimetables;

  const [rooms, setRooms] = useState<ExamRoom[]>([]);
  const [schoolRooms, setSchoolRooms] = useState<string[]>([]);
  /* Only for the printed plan's signature block — the rota itself is the
     Invigilation pane's business, and this never writes it. */
  const [duties, setDuties] = useState<{ date: string; session: string; code: string; subject: string; start: string; end: string; names: string[] }[]>([]);
  const [plans, setPlans] = useState<Record<string, SeatingDoc>>({});
  const [entries, setEntries] = useState<PupilExamEntries[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<'sessions' | 'rooms'>('sessions');
  const [sessionKey, setSessionKey] = useState('');

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [roomSnap, planSnap, entrySnap, schoolSnap, dutySnap] = await Promise.all([
          getDocs(collection(db, 'reportExamRooms')),
          getDocs(query(collection(db, 'reportExamSeating'), where('seriesId', '==', seriesId))),
          getDocs(query(collection(db, 'reportExamEntries'), where('seriesId', '==', seriesId))),
          // The timetable's own room list, so exam rooms are picked rather
          // than typed. Its absence is not an error: the four exam rooms are
          // offered regardless.
          getDoc(doc(db, 'timetableRooms', 'list')).catch(() => null),
          getDocs(query(collection(db, 'reportExamDuties'), where('seriesId', '==', seriesId))).catch(() => null),
        ]);
        if (!live) return;
        setSchoolRooms((schoolSnap?.data()?.list as string[] | undefined) ?? []);
        setDuties((dutySnap?.docs ?? []).map((d) => {
          const x = d.data() as {
            date?: string; session?: string; code?: string; subject?: string; start?: string; end?: string;
            duties?: { teacherName?: string }[];
          };
          return {
            date: String(x.date ?? ''),
            session: String(x.session ?? ''),
            code: String(x.code ?? ''),
            subject: String(x.subject ?? ''),
            start: String(x.start ?? ''),
            end: String(x.end ?? ''),
            names: (x.duties ?? []).map((t) => String(t.teacherName ?? '')).filter(Boolean),
          };
        }));
        setRooms(roomSnap.docs.map((d) => ({ ...(d.data() as ExamRoom), id: d.id })));
        setPlans(Object.fromEntries(planSnap.docs.map((d) => [d.id, { ...(d.data() as SeatingDoc), id: d.id }])));
        setEntries(entrySnap.docs.map((d) => d.data() as PupilExamEntries));
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : 'Could not load the seating.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [seriesId]);

  const candidates = useMemo(() => {
    const classOf = new Map((students ?? []).map((s) => [s.id, s.currentClass || '']));
    return entries.map((e) => ({ ...e, className: classOf.get(e.studentId) || '' }));
  }, [entries, students]);

  /** Every session the series actually sits, off the statements of entry. */
  const sessions = useMemo(() => {
    const by = new Map<string, { key: string; date: string; session: string; people: SeatingCandidate[] }>();
    for (const c of candidates) {
      for (const comp of c.components ?? []) {
        const session = comp.session || 'AM';
        const key = `${comp.date}|${session}`;
        if (by.has(key)) continue;
        by.set(key, { key, date: comp.date, session, people: sessionCandidates(candidates, comp.date, session) });
      }
    }
    return [...by.values()].sort((a, b) => a.date.localeCompare(b.date) || a.session.localeCompare(b.session));
  }, [candidates]);

  useEffect(() => {
    if (!sessionKey && sessions.length) setSessionKey(sessions[0].key);
  }, [sessions, sessionKey]);

  const current = sessions.find((s) => s.key === sessionKey) ?? null;
  const planId = current ? seatingId(seriesId, current.date, current.session) : '';
  const plan = plans[planId] ?? null;
  const chosenRooms = useMemo(
    () => (plan?.roomIds ?? []).map((id) => rooms.find((r) => r.id === id)).filter(Boolean) as ExamRoom[],
    [plan, rooms],
  );

  const savePlan = async (next: Partial<SeatingDoc>) => {
    if (!current) return;
    const merged: SeatingDoc = {
      id: planId, seriesId, date: current.date, session: current.session,
      roomIds: plan?.roomIds ?? [], seats: plan?.seats ?? {}, ...next,
      updatedAt: new Date().toISOString(),
    };
    setPlans((p) => ({ ...p, [planId]: merged }));
    try {
      await setDoc(doc(db, 'reportExamSeating', planId), { ...merged, updatedBy: userData?.uid ?? '' });
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That change was not saved.');
    }
  };

  if (loading) return <p className="py-10 text-sm text-[var(--sp-muted)]">Loading the rooms…</p>;

  const seatedTotal = Object.values(plans).reduce((n, p) => n + Object.keys(p.seats ?? {}).length, 0);
  const planned = Object.values(plans).filter((p) => Object.keys(p.seats ?? {}).length).length;

  return (
    <div className="space-y-5">
      <StatStrip>
        <StatChip tone="navy" icon={DoorOpen} value={rooms.length} label={`rooms · ${totalCapacity(rooms)} desks`} />
        <StatChip tone="sky" icon={Grid3x3} value={`${planned}/${sessions.length}`} label="sessions with a plan" />
        <StatChip tone="slate" icon={Users} value={seatedTotal} label="candidates seated" />
        <StatChip
          tone={current && current.people.length > totalCapacity(chosenRooms) ? 'rose' : 'emerald'}
          value={current?.people.length ?? 0}
          label={current ? `to seat on ${shortDay(current.date)} ${sessionText(current.session)}` : 'nothing selected'}
        />
      </StatStrip>

      {error && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setView('sessions')}
          className={view === 'sessions' ? btnPrimary : btnGhost}>Seating plans</button>
        <button type="button" onClick={() => setView('rooms')}
          className={view === 'rooms' ? btnPrimary : btnGhost}>Rooms</button>
      </div>

      {view === 'rooms' ? (
        <RoomList rooms={rooms} canEdit={canEdit} onChange={setRooms} onError={setError} />
      ) : !rooms.length ? (
        <SoftCard pad="">
          <p className="px-5 py-10 text-center text-sm text-[var(--sp-muted)]">
            No exam room has been recorded yet. Add one under <strong>Rooms</strong> — a plan needs to know
            how many desks there are and where they sit.
          </p>
        </SoftCard>
      ) : (
        <SessionPlan
          duties={duties}
          schoolRooms={schoolRooms}
          onAddRoom={async (room) => {
            await setDoc(doc(db, 'reportExamRooms', room.id), room);
            setRooms((cur) => [...cur.filter((r) => r.id !== room.id), room]
              .sort((a, b) => a.name.localeCompare(b.name)));
          }}
          sessions={sessions}
          sessionKey={sessionKey}
          onPick={setSessionKey}
          rooms={rooms}
          chosenRooms={chosenRooms}
          plan={plan}
          canEdit={canEdit}
          seriesTitle={seriesTitle}
          sessionText={sessionText}
          onSave={(next) => { void savePlan(next); }}
        />
      )}
    </div>
  );
}

/* ── the rooms ────────────────────────────────────────────────────── */

function RoomList({ rooms, canEdit, onChange, onError }: {
  rooms: ExamRoom[];
  canEdit: boolean;
  onChange: (rooms: ExamRoom[]) => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState<ExamRoom | null>(null);

  const save = async (room: ExamRoom) => {
    try {
      await setDoc(doc(db, 'reportExamRooms', room.id), room);
      onChange([...rooms.filter((r) => r.id !== room.id), room].sort((a, b) => a.name.localeCompare(b.name)));
      setEditing(null);
    } catch (e) { onError(e instanceof Error ? e.message : 'The room was not saved.'); }
  };
  const remove = async (room: ExamRoom) => {
    try {
      await deleteDoc(doc(db, 'reportExamRooms', room.id));
      onChange(rooms.filter((r) => r.id !== room.id));
    } catch (e) { onError(e instanceof Error ? e.message : 'The room was not removed.'); }
  };

  return (
    <SoftCard pad="">
      <PanelHeader
        icon={DoorOpen} title="Exam rooms"
        subtitle="Rows and columns of desks — a plan is a map of the room, not a headcount"
        right={canEdit ? (
          <button type="button" className={`${btnGhost} px-3 py-1.5 text-xs`}
            onClick={() => setEditing({ id: `room-${Date.now()}`, name: '', rows: 6, cols: 5, unusable: [], active: true })}>
            <Plus className="h-3.5 w-3.5" /> Add a room
          </button>
        ) : undefined}
      />
      {editing && <RoomEditor room={editing} onCancel={() => setEditing(null)} onSave={(r) => { void save(r); }} />}
      <ul className="divide-y divide-[var(--sp-line)]">
        {rooms.map((room) => (
          <li key={room.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink dark:text-slate-100">
                {room.name}
                {room.active === false && <span className="ml-2 text-xs text-[var(--sp-muted)]">(not in use)</span>}
              </p>
              <p className="text-xs text-[var(--sp-muted)]">
                {room.rows} rows × {room.cols} = {roomCapacity(room)} usable desk{roomCapacity(room) === 1 ? '' : 's'}
                {room.unusable?.length ? ` · ${room.unusable.length} marked unusable` : ''}
                {room.notes ? ` · ${room.notes}` : ''}
              </p>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <button type="button" className={`${btnGhost} px-2.5 py-1 text-xs`} onClick={() => setEditing(room)}>Edit</button>
                <button
                  type="button" className="rounded-lg p-1.5 text-[var(--sp-muted)] hover:bg-[var(--sp-line)] hover:text-rose-600"
                  aria-label={`Remove ${room.name}`} onClick={() => { void remove(room); }}
                ><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </li>
        ))}
        {!rooms.length && !editing && (
          <li className="px-5 py-10 text-center text-sm text-[var(--sp-muted)]">No rooms yet.</li>
        )}
      </ul>
    </SoftCard>
  );
}

function RoomEditor({ room, onCancel, onSave }: {
  room: ExamRoom; onCancel: () => void; onSave: (room: ExamRoom) => void;
}) {
  const [draft, setDraft] = useState<ExamRoom>({ ...room, unusable: room.unusable ?? [] });
  const dead = new Set(draft.unusable ?? []);
  const toggle = (row: number, col: number) => {
    const key = deskKey(row, col);
    const next = new Set(dead);
    if (next.has(key)) next.delete(key); else next.add(key);
    setDraft({ ...draft, unusable: [...next] });
  };

  return (
    <div className="border-b border-[var(--sp-line)] bg-[var(--sp-paper)] p-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-[var(--sp-muted)]">
          Name
          <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Exam Hall" className={glassFieldClass('mt-1 w-48')} />
        </label>
        <label className="text-xs font-semibold text-[var(--sp-muted)]">
          Rows
          <input type="number" min={1} max={20} value={draft.rows}
            onChange={(e) => setDraft({ ...draft, rows: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })}
            className={glassFieldClass('mt-1 w-20')} />
        </label>
        <label className="text-xs font-semibold text-[var(--sp-muted)]">
          Columns
          <input type="number" min={1} max={20} value={draft.cols}
            onChange={(e) => setDraft({ ...draft, cols: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })}
            className={glassFieldClass('mt-1 w-20')} />
        </label>
        <label className="text-xs font-semibold text-[var(--sp-muted)]">
          Note
          <input value={draft.notes ?? ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            placeholder="upstairs, no power" className={glassFieldClass('mt-1 w-56')} />
        </label>
        <label className="inline-flex items-center gap-1.5 pb-2 text-xs font-semibold text-[var(--sp-muted)]">
          <input type="checkbox" checked={draft.active !== false}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
          In use for exams
        </label>
      </div>

      <p className="mb-1.5 mt-3 text-xs text-[var(--sp-muted)]">
        Tap a desk to mark it unusable — a pillar, a broken chair, or a gap left deliberately for spacing.
        Row 1 is the front. <strong className="text-[var(--sp-body)]">{roomCapacity(draft)} usable</strong>.
      </p>
      <div className="inline-block overflow-x-auto rounded-lg border border-[var(--sp-line)] bg-[var(--sp-card)] p-2">
        {Array.from({ length: draft.rows }, (_, r) => (
          <div key={r} className="flex gap-1 py-0.5">
            {Array.from({ length: draft.cols }, (_, c) => {
              const isDead = dead.has(deskKey(r + 1, c + 1));
              return (
                <button
                  key={c} type="button" onClick={() => toggle(r + 1, c + 1)}
                  aria-label={`Row ${r + 1}, column ${c + 1}${isDead ? ', unusable' : ''}`}
                  className={`h-7 w-7 rounded border text-[10px] ${isDead
                    ? 'border-rose-300 bg-rose-100 text-rose-700 line-through dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-300'
                    : 'border-[var(--sp-line)] bg-[var(--sp-paper)] text-[var(--sp-muted)] hover:border-primaryLight'}`}
                >
                  {isDead ? '×' : ''}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button type="button" className={btnPrimary} disabled={!draft.name.trim()}
          onClick={() => onSave({ ...draft, name: draft.name.trim() })}>
          <Save className="h-4 w-4" /> Save room
        </button>
        <button type="button" className={btnGhost} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── one session's plan ───────────────────────────────────────────── */

function SessionPlan({
  sessions, sessionKey, onPick, rooms, chosenRooms, plan, canEdit, seriesTitle, sessionText, onSave,
  schoolRooms, onAddRoom, duties,
}: {
  schoolRooms: string[];
  duties: { date: string; session: string; code: string; subject: string; start: string; end: string; names: string[] }[];
  onAddRoom: (room: ExamRoom) => Promise<void>;
  sessions: { key: string; date: string; session: string; people: SeatingCandidate[] }[];
  sessionKey: string;
  onPick: (key: string) => void;
  rooms: ExamRoom[];
  chosenRooms: ExamRoom[];
  plan: SeatingDoc | null;
  canEdit: boolean;
  seriesTitle: string;
  sessionText: (session: string) => string;
  onSave: (next: Partial<SeatingDoc>) => void;
}) {
  const [picked, setPicked] = useState('');
  const [adding, setAdding] = useState(false);
  /* The desks filled by hand since this session was opened, in the order they
     were filled — what detectFillPattern reads. Deliberately not stored: it
     describes this sitting at the screen, not the plan. */
  const [dropLog, setDropLog] = useState<{ roomId: string; desk: number }[]>([]);
  /* UNDO (the school, 2026-10-15). Every change to who sits where, or to the
     desks left empty, is kept for this sitting at the screen — fifty back —
     and Ctrl+Z / Ctrl+Y walk it. Not stored: it describes the session at the
     screen, not the plan. */
  type Snapshot = { seats: Record<string, Seat>; blocked: string[] };
  const [history, setHistory] = useState<{ undo: Snapshot[]; redo: Snapshot[] }>({ undo: [], redo: [] });
  /* Leaving desks empty for this session: while on, a tap marks a desk. */
  const [blocking, setBlocking] = useState(false);
  /* What the last automatic plan did, said once beside its buttons. */
  const [note, setNote] = useState('');
  useEffect(() => {
    setDropLog([]); setPicked(''); setHistory({ undo: [], redo: [] }); setBlocking(false); setNote('');
  }, [sessionKey]);
  const current = sessions.find((s) => s.key === sessionKey);
  const seats = plan?.seats ?? {};
  const blocked = plan?.blocked ?? [];
  const people = current?.people ?? [];
  const problems = seatingProblems(people, chosenRooms, seats);
  const capacity = totalCapacity(chosenRooms) - blocked.filter((k) => chosenRooms.some((r) => k.startsWith(`${r.id}|`))).length;

  /** Every change to the seating goes through here, so it can be undone. */
  const change = (next: Partial<Snapshot>, extra: Partial<SeatingDoc> = {}) => {
    setHistory((h) => ({ undo: [...h.undo.slice(-49), { seats, blocked }], redo: [] }));
    setNote('');
    onSave({ ...next, ...extra });
  };
  const undo = () => {
    const prev = history.undo[history.undo.length - 1];
    if (!prev) return;
    setHistory((h) => ({ undo: h.undo.slice(0, -1), redo: [...h.redo, { seats, blocked }] }));
    onSave(prev);
    setPicked(''); setNote('');
  };
  const redo = () => {
    const nxt = history.redo[history.redo.length - 1];
    if (!nxt) return;
    setHistory((h) => ({ undo: [...h.undo, { seats, blocked }], redo: h.redo.slice(0, -1) }));
    onSave(nxt);
    setPicked(''); setNote('');
  };
  useEffect(() => {
    if (!canEdit) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
      else return;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const options: AutoOptions = {
    pattern: plan?.fillPattern ?? 'reading',
    spacing: plan?.spacing ?? 'spread',
    order: plan?.order ?? 'number',
    blocked,
    limits: plan?.limits,
    paperOrder: plan?.paperOrder,
    freshLinePerPaper: plan?.freshLinePerPaper,
  };

  /** The automatic plan: everyone still standing, or everybody afresh. */
  const allocate = (fresh: boolean) => {
    const res = autoSeat(people, chosenRooms, fresh ? {} : seats, options);
    change({ seats: res.seats });
    const placed = Object.keys(res.seats).length - (fresh ? 0 : Object.keys(seats).filter((id) => res.seats[id]).length);
    setNote(res.unseated.length
      ? `${res.unseated.length} could not be seated with this spacing. Choose closer spacing, or add a room.`
      : `${placed} seated · ${SPACING_LABEL[res.spacing].toLowerCase()}.`);
  };

  const clearPlan = () => {
    change({ seats: {} });
    setNote('The plan is empty. Undo brings it back.');
  };

  /** Seat one candidate on one desk — the end of a drag, or of two taps. */
  const seatOn = (studentId: string, seat: Seat) => {
    if (seats[studentId]?.roomId === seat.roomId && seats[studentId]?.desk === seat.desk) { setPicked(''); return; }
    change({ seats: moveSeat(seats, studentId, seat) });
    setDropLog((log) => [...log, { roomId: seat.roomId, desk: seat.desk }]);
    setPicked('');
  };

  const sendBack = (studentId: string) => {
    if (!seats[studentId]) return;
    change({ seats: unseat(seats, studentId) });
    setPicked('');
  };

  /** Leave a desk empty for this session, or use it again. Anyone on it goes back to the list. */
  const toggleBlocked = (room: ExamRoom, row: number, col: number, occupant?: string) => {
    const key = blockKey(room.id, row, col);
    const on = blocked.includes(key);
    change({
      blocked: on ? blocked.filter((k) => k !== key) : [...blocked, key],
      seats: occupant && !on ? unseat(seats, occupant) : seats,
    });
  };

  /* What the hand is doing, read off the room it is doing it in. */
  const lastRoomId = dropLog.length ? dropLog[dropLog.length - 1].roomId : '';
  const lastRoom = chosenRooms.find((r) => r.id === lastRoomId);
  const guess = lastRoom
    ? detectFillPattern(lastRoom, dropLog.filter((d) => d.roomId === lastRoom.id).map((d) => d.desk))
    : null;
  /* A hall often sits several papers at once. Each is its own list on the
     left and its own printable plan; the seating itself is one plan, because
     a desk is a desk. */
  const papers = useMemo(() => {
    const count = new Map<string, number>();
    people.forEach((c) => c.codes.forEach((code) => count.set(code, (count.get(code) ?? 0) + 1)));
    return [...count.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([code, n]) => ({ code, count: n }));
  }, [people]);

  /* One colour per paper, the same table the printed plan uses. A session
     with a single paper gets none: colour that distinguishes nothing is
     decoration. */
  const colours = useMemo(() => paperColours(people.flatMap((c) => c.codes)), [people]);

  const invigilatorsFor = (code?: string) => {
    if (!current) return [];
    const rows = duties.filter((d) => d.date === current.date
      && (!d.session || !current.session || d.session === current.session)
      && (!code || d.code === code));
    return [...new Set(rows.flatMap((r) => r.names))];
  };

  const standing = people.filter((c) => !seats[c.studentId]);
  const offer = guess && standing.length ? guess : null;
  /* Who a tap on an empty desk seats when nobody is picked: the first card in
     the list as it is drawn, paper by paper when the room sits several. That
     is what makes seating by hand quick — tap the desks in order. */
  const queue = useMemo(() => {
    if (papers.length < 2) return standing;
    const seen = new Set<string>();
    return papers.flatMap((p) => standing.filter((c) => c.codes.includes(p.code)))
      .filter((c) => !seen.has(c.studentId) && !!seen.add(c.studentId));
  }, [papers, standing]);
  const next = queue[0] ?? null;
  const pickedSeated = picked && seats[picked] ? people.find((c) => c.studentId === picked) ?? null : null;

  /* The exams in the order they are seated ("Paper by paper"): the saved
     order, then any exam it does not name yet, by code. */
  const examOrder = useMemo(() => {
    const codes = papers.map((p) => p.code);
    const saved = (plan?.paperOrder ?? []).filter((c) => codes.includes(c));
    return [...saved, ...codes.filter((c) => !saved.includes(c))];
  }, [papers, plan?.paperOrder]);
  const moveExam = (i: number, by: -1 | 1) => {
    const next = [...examOrder];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    onSave({ paperOrder: next });
  };

  const finishByPattern = (pattern: FillPattern) => {
    // The room being filled is finished first, then the others in their order.
    // Every desk: the hand that set the pattern was filling every desk.
    const ordered = lastRoom
      ? [lastRoom, ...chosenRooms.filter((r) => r.id !== lastRoom.id)]
      : chosenRooms;
    const { seats: done } = autoSeat(people, ordered, seats, { ...options, pattern, spacing: 'every', order: 'number' });
    change({ seats: done }, { fillPattern: pattern });
  };

  const moveRoom = (id: string, by: -1 | 1) => {
    const ids = [...(plan?.roomIds ?? [])];
    const i = ids.indexOf(id);
    const j = i + by;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    onSave({ roomIds: ids });
  };

  const toggleRoom = (room: ExamRoom) => {
    const ids = plan?.roomIds ?? [];
    onSave({ roomIds: ids.includes(room.id) ? ids.filter((i) => i !== room.id) : [...ids, room.id] });
  };

  const print = async (what: 'plan' | 'labels', code?: string) => {
    if (!current) return;
    const { buildSeatingPlanPdf, buildDeskLabelsPdf } = await import('@/lib/examSeatingPdf');
    const input = {
      seriesTitle, date: current.date, session: sessionText(current.session),
      dayLabel: shortDay(current.date), rooms: chosenRooms, candidates: people, seats,
      invigilators: invigilatorsFor(code),
      papers: code ? [code] : undefined,
      paperLabel: code,
    };
    const pdf = what === 'plan' ? buildSeatingPlanPdf(input) : buildDeskLabelsPdf(input);
    const tag = code ? `-${code.replace('/', '-')}` : '';
    pdf.save(`${what === 'plan' ? 'seating-plan' : 'desk-labels'}-${current.date}-${current.session}${tag}.pdf`);
  };

  /** The plan as the Cambridge Handbook asks for it: a page per room, signed. */
  const printCambridge = async () => {
    if (!current) return;
    const { buildCambridgeSeatingPlanPdf } = await import('@/lib/examSeatingPdf');
    const rota = duties.filter((d) => d.date === current.date
      && (!d.session || !current.session || d.session === current.session));
    const papersInSession = papers.map(({ code }) => {
      const row = rota.find((d) => d.code === code);
      return { code, title: row?.subject, start: row?.start, end: row?.end };
    });
    const dateLong = new Date(`${current.date}T12:00:00`).toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    const pdf = buildCambridgeSeatingPlanPdf({
      seriesTitle, date: current.date, session: sessionText(current.session), dayLabel: shortDay(current.date),
      dateLong, rooms: chosenRooms, candidates: people, seats, blocked,
      invigilators: invigilatorsFor(), papersInSession,
      invigilatorsByCode: Object.fromEntries(papers.map(({ code }) => [code, invigilatorsFor(code)])),
    });
    pdf.save(`cambridge-seating-plan-${current.date}-${current.session}.pdf`);
  };

  /* Proposal A, "the workbench" (the school, 2026-09-22): candidates left,
     the room in the middle with the room of the screen, the controls gathered
     in a column on the right instead of competing with the plan. "Focus the
     room" collapses both sides for a big hall — proposal B's one advantage,
     without a second screen to keep working. */
  const [focus, setFocus] = useState(false);
  const seatedCount = people.filter((c) => seats[c.studentId]).length;

  return (
    <div className="space-y-4">
      {/* ── the session, its papers, and printing ── */}
      <SoftCard pad="">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          <select value={sessionKey} onChange={(e) => onPick(e.target.value)}
            className={glassFieldClass('w-auto min-w-[16rem] font-semibold')} aria-label="Which session">
            {sessions.map((s) => (
              <option key={s.key} value={s.key}>
                {shortDay(s.date)} · {sessionText(s.session)} — {s.people.length} candidate{s.people.length === 1 ? '' : 's'}
              </option>
            ))}
          </select>

          {/* The key, where both the list and the plan can be seen against it. */}
          {colours.size > 1 && (
            <div className="flex flex-wrap items-center gap-2.5 text-[11px]">
              {[...colours.values()].map((colour) => (
                <span key={colour.code} className="inline-flex items-center gap-1.5 font-semibold text-[var(--sp-body)]">
                  <span className="inline-block h-3 w-3 rounded-sm border"
                    style={{ backgroundColor: colour.fill, borderColor: colour.ink }} />
                  {colour.code}
                  <span className="font-normal text-[var(--sp-muted)]">
                    ({papers.find((x) => x.code === colour.code)?.count ?? 0})
                  </span>
                </span>
              ))}
            </div>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {canEdit && (
              <>
                <button type="button" className={`${btnGhost} px-3 py-1.5 text-xs`} onClick={undo}
                  disabled={!history.undo.length} title="Undo (Ctrl+Z)">
                  <Undo2 className="h-3.5 w-3.5" /> Undo
                </button>
                <button type="button" className={`${btnGhost} px-3 py-1.5 text-xs`} onClick={redo}
                  disabled={!history.redo.length} title="Redo (Ctrl+Y)">
                  <Redo2 className="h-3.5 w-3.5" /> Redo
                </button>
              </>
            )}
            <button type="button" className={`${btnGhost} px-3 py-1.5 text-xs`}
              onClick={() => setFocus((v) => !v)} aria-pressed={focus}>
              {focus ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              {focus ? 'Show the lists' : 'Focus the room'}
            </button>
            <button type="button" className={`${btnGhost} px-3 py-1.5 text-xs`} onClick={() => { void print('plan'); }}>
              <FileDown className="h-3.5 w-3.5" /> Seating plan
            </button>
            <button type="button" className={`${btnGhost} px-3 py-1.5 text-xs`} onClick={() => { void printCambridge(); }}
              title="A page per room as the Cambridge Handbook asks: tables and chairs, the invigilator's desk, the way candidates face, and space to sign">
              <FileDown className="h-3.5 w-3.5" /> Cambridge plan
            </button>
            {papers.length > 1 && (
              <select
                aria-label="Print the plan for one paper"
                className={glassFieldClass('w-auto py-1 text-xs')}
                value=""
                onChange={(e) => { if (e.target.value) { void print('plan', e.target.value); e.target.value = ''; } }}
              >
                <option value="">Plan for one paper…</option>
                {papers.map((p) => <option key={p.code} value={p.code}>{p.code} ({p.count})</option>)}
              </select>
            )}
            <button type="button" className={`${btnGhost} px-3 py-1.5 text-xs`} onClick={() => { void print('labels'); }}>
              <FileDown className="h-3.5 w-3.5" /> Desk labels
            </button>
          </div>
        </div>
      </SoftCard>

      {/* ── the offer, across the whole width: it is the one thing that
             interrupts, and it must not be missed in a side column ── */}
      {offer && canEdit && (
        <SoftCard pad="">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <Wand2 className="h-5 w-5 shrink-0 text-primary" />
            <p className="min-w-[14rem] flex-1 text-sm text-[var(--sp-body)]">
              You are seating <strong>{FILL_PATTERN_LABEL[offer.pattern].toLowerCase()}</strong>.
              Carry on that way for the {standing.length} candidate{standing.length === 1 ? '' : 's'} still standing?
            </p>
            <button type="button" className={btnPrimary} onClick={() => finishByPattern(offer.pattern)}>
              <Sparkles className="h-4 w-4" /> Finish this way
            </button>
            <button type="button" className={`${btnGhost} px-3 py-1.5 text-xs`} onClick={() => setDropLog([])}>
              No, I&apos;ll carry on
            </button>
          </div>
        </SoftCard>
      )}

      <div className={`grid gap-4 ${focus ? '' : 'xl:grid-cols-[15rem_minmax(0,1fr)_17rem]'}`}>
        {/* ── left: who is still standing ── */}
        {!focus && (
          <CandidateList
            people={people} seats={seats} picked={picked} canEdit={canEdit} papers={papers} colours={colours}
            nextId={next?.studentId ?? ''}
            onPick={(id) => setPicked(id === picked ? '' : id)}
            onSendBack={sendBack}
          />
        )}

        {/* ── middle: the rooms ── */}
        <div className="min-w-0 space-y-4">
          {canEdit && !!chosenRooms.length && (
            <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${blocking
              ? 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200'
              : 'border-[var(--sp-line)] bg-[var(--sp-card)] text-[var(--sp-body)]'}`}>
              <span className="min-w-[12rem] flex-1">
                {blocking
                  ? 'Tap the desks to leave empty in this session. Tap again to use one.'
                  : pickedSeated
                    ? <>Tap another desk to move <strong>{pickedSeated.candidateNumber}</strong>, or send them back to the list.</>
                    : picked
                      ? 'Tap a desk to seat the chosen candidate.'
                      : next
                        ? <>Tap an empty desk to seat <strong>{next.candidateNumber}</strong> (next on the list), or drag any card. Tap a seated candidate to move them.</>
                        : 'Everybody is seated. Tap a candidate to move them.'}
              </span>
              {pickedSeated && !blocking && (
                <button type="button" className={`${btnGhost} px-2.5 py-1 text-xs`} onClick={() => sendBack(pickedSeated.studentId)}>
                  <X className="h-3.5 w-3.5" /> Back to the list
                </button>
              )}
              {picked && !blocking && (
                <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => setPicked('')}>
                  Cancel
                </button>
              )}
              <button type="button" aria-pressed={blocking}
                className={`${blocking ? btnPrimary : btnGhost} px-2.5 py-1 text-xs`}
                onClick={() => { setBlocking((v) => !v); setPicked(''); }}>
                <Ban className="h-3.5 w-3.5" /> {blocking ? 'Done' : 'Leave desks empty'}
              </button>
            </div>
          )}
          {chosenRooms.map((room) => (
            <RoomGrid
              key={room.id} room={room} people={people} seats={seats} picked={picked} canEdit={canEdit}
              colours={colours}
              blocked={blocked}
              blocking={blocking}
              onToggleBlock={(row, col, occupant) => toggleBlocked(room, row, col, occupant)}
              onPick={setPicked}
              onRoomChange={(nextRoom) => { void onAddRoom(nextRoom); }}
              onSeat={(seat, studentId) => {
                const who = studentId || picked || next?.studentId;
                if (who) seatOn(who, seat);
              }}
            />
          ))}
          {!chosenRooms.length && (
            <SoftCard className="text-sm text-[var(--sp-muted)]">
              Add a room on the right, then drag candidates onto the desks.
            </SoftCard>
          )}
        </div>

        {/* ── right: the state of this session, and what changes it ── */}
        {!focus && (
          <div className="space-y-3 xl:sticky xl:top-4 xl:self-start">
            <SoftCard pad="p-3">
              <p className="text-sm font-bold text-ink dark:text-slate-100">
                {seatedCount} of {people.length} seated
              </p>
              <p className="text-xs text-[var(--sp-muted)]">
                {standing.length ? `${standing.length} still standing` : 'Everybody has a desk'}
                {capacity ? ` · ${capacity} desk${capacity === 1 ? '' : 's'} in ${chosenRooms.length} room${chosenRooms.length === 1 ? '' : 's'}` : ''}
              </p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--sp-paper)]">
                <span className="block h-full rounded-full bg-primary transition-all"
                  style={{ width: `${people.length ? Math.round((seatedCount / people.length) * 100) : 0}%` }} />
              </div>
            </SoftCard>

            <SoftCard pad="p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--sp-muted)]">Rooms</p>
              <div className="flex flex-col gap-2">
                {chosenRooms.length > 1 && (
                  <p className="text-[11px] text-[var(--sp-muted)]">Filled in this order. Use the arrows to change it.</p>
                )}
                {chosenRooms.map((room, i) => (
                  <ChosenRoom
                    key={room.id} room={room} canEdit={canEdit}
                    limit={plan?.limits?.[room.id]}
                    first={i === 0} last={i === chosenRooms.length - 1}
                    onMove={(by) => moveRoom(room.id, by)}
                    onLimit={(n) => {
                      const limits = { ...(plan?.limits ?? {}) };
                      if (n === undefined) delete limits[room.id]; else limits[room.id] = n;
                      onSave({ limits });
                    }}
                    onResize={(cols, rows) => { void onAddRoom({ ...room, cols, rows }); }}
                    onRemove={() => toggleRoom(room)}
                  />
                ))}
                {canEdit && (
                  <button type="button" onClick={() => setAdding((v) => !v)}
                    className="rounded-lg border-2 border-dashed border-[var(--sp-line)] px-3 py-2 text-xs font-semibold text-[var(--sp-muted)] hover:border-primaryLight hover:text-primary">
                    <Plus className="mr-1 inline h-3.5 w-3.5" /> Add a room
                  </button>
                )}
              </div>
              {adding && canEdit && (
                <RoomPicker
                  schoolRooms={schoolRooms}
                  rooms={rooms}
                  chosenIds={plan?.roomIds ?? []}
                  onClose={() => setAdding(false)}
                  onChoose={async (name) => {
                    const known = rooms.find((r) => r.name.toLowerCase() === name.toLowerCase());
                    if (known) { if (!(plan?.roomIds ?? []).includes(known.id)) toggleRoom(known); setAdding(false); return; }
                    const { cols, rows } = suggestedDesks(name);
                    const room: ExamRoom = { id: `room_${Date.now().toString(36)}`, name, rows, cols, active: true };
                    await onAddRoom(room);
                    onSave({ roomIds: [...(plan?.roomIds ?? []), room.id] });
                    setAdding(false);
                  }}
                />
              )}
            </SoftCard>

            {canEdit && (
              <SoftCard pad="p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--sp-muted)]">Automatic plan</p>
                <div className="space-y-2">
                  <label className="block text-[11px] font-semibold text-[var(--sp-muted)]">
                    Spacing
                    <select
                      value={plan?.spacing ?? 'spread'}
                      onChange={(e) => onSave({ spacing: e.target.value as Spacing })}
                      className={glassFieldClass('mt-1 text-xs')}
                    >
                      {(['spread', 'every', 'besides', 'rows', 'chess'] as Spacing[]).map((f) => (
                        <option key={f} value={f}>{SPACING_LABEL[f]}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-[11px] font-semibold text-[var(--sp-muted)]">
                    Fill order
                    <select
                      value={plan?.fillPattern ?? 'reading'}
                      onChange={(e) => onSave({ fillPattern: e.target.value as FillPattern })}
                      className={glassFieldClass('mt-1 text-xs')}
                      aria-label="The order desks are filled in"
                    >
                      {(['reading', 'rows', 'columns'] as FillPattern[]).map((f) => (
                        <option key={f} value={f}>{FILL_PATTERN_LABEL[f]}</option>
                      ))}
                    </select>
                  </label>
                  {papers.length > 1 && (
                    <label className="block text-[11px] font-semibold text-[var(--sp-muted)]">
                      Papers
                      <select
                        value={plan?.order ?? 'number'}
                        onChange={(e) => onSave({ order: e.target.value as SeatOrder })}
                        className={glassFieldClass('mt-1 text-xs')}
                      >
                        {(['number', 'paper', 'alternate'] as SeatOrder[]).map((f) => (
                          <option key={f} value={f}>{SEAT_ORDER_LABEL[f]}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {papers.length > 1 && (plan?.order ?? 'number') === 'paper' && (
                    <div className="rounded-lg border border-[var(--sp-line)] bg-[var(--sp-paper)] p-2">
                      <p className="mb-1 text-[11px] font-semibold text-[var(--sp-muted)]">Seat the exams in this order</p>
                      <ol className="space-y-1">
                        {examOrder.map((code, i) => (
                          <li key={code} className="flex items-center gap-1.5 text-xs text-[var(--sp-body)]">
                            <span className="w-4 text-right tabular-nums text-[var(--sp-muted)]">{i + 1}</span>
                            <span className="min-w-0 flex-1 truncate font-semibold">
                              {code} <span className="font-normal text-[var(--sp-muted)]">({papers.find((p) => p.code === code)?.count ?? 0})</span>
                            </span>
                            <button type="button" aria-label={`Seat ${code} earlier`} disabled={i === 0}
                              onClick={() => moveExam(i, -1)} className="text-[var(--sp-muted)] hover:text-primary disabled:opacity-30">
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" aria-label={`Seat ${code} later`} disabled={i === examOrder.length - 1}
                              onClick={() => moveExam(i, 1)} className="text-[var(--sp-muted)] hover:text-primary disabled:opacity-30">
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ol>
                      <label className="mt-2 flex items-start gap-1.5 text-[11px] text-[var(--sp-body)]">
                        <input type="checkbox" className="mt-0.5" checked={!!plan?.freshLinePerPaper}
                          onChange={(e) => onSave({ freshLinePerPaper: e.target.checked })} />
                        <span>
                          Start each exam at the front of a fresh {(plan?.fillPattern ?? 'reading') === 'columns' ? 'column' : 'row'}
                          <span className="block text-[var(--sp-muted)]">while the room has desks to spare; otherwise the next exam carries straight on.</span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex flex-col gap-2">
                  <button type="button" className={btnPrimary} disabled={!chosenRooms.length || !standing.length}
                    onClick={() => allocate(false)}>
                    <Sparkles className="h-4 w-4" /> {!standing.length ? 'Everybody is seated' : Object.keys(seats).length ? `Seat the ${standing.length} still standing` : 'Seat everybody'}
                  </button>
                  <button type="button" className={`${btnGhost} text-xs`} disabled={!chosenRooms.length || !Object.keys(seats).length}
                    onClick={() => allocate(true)}>
                    <RotateCcw className="h-3.5 w-3.5" /> Re-seat everybody
                  </button>
                  <button type="button" className={`${btnGhost} text-xs`} disabled={!Object.keys(seats).length} onClick={clearPlan}>
                    <Eraser className="h-3.5 w-3.5" /> Clear the plan
                  </button>
                </div>
                {note && <p className="mt-2 rounded bg-[var(--sp-paper)] px-2 py-1.5 text-[11px] text-[var(--sp-body)]">{note}</p>}
                <p className="mt-2 text-[11px] text-[var(--sp-muted)]">
                  Seating keeps anyone already placed. Re-seat moves everybody; Clear empties every desk.
                  Undo (Ctrl+Z) takes back any of them.
                </p>
              </SoftCard>
            )}

            {!!problems.length && (
              <SoftCard pad="">
                <ul className="divide-y divide-[var(--sp-line)]">
                  {problems.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 px-3 py-2 text-xs text-[var(--sp-body)]">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                      {p.detail}
                    </li>
                  ))}
                </ul>
              </SoftCard>
            )}

            {!problems.length && !!Object.keys(seats).length && (
              <p className="inline-flex items-center gap-1.5 px-1 text-xs text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> Everybody has a desk.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── a chosen room: how many desks wide and deep ──────────────────── */

function ChosenRoom({ room, canEdit, onResize, onRemove, limit, onLimit, first, last, onMove }: {
  room: ExamRoom; canEdit: boolean; onResize: (cols: number, rows: number) => void; onRemove: () => void;
  /** At most this many candidates here; undefined is as many as fit. */
  limit?: number;
  onLimit: (n: number | undefined) => void;
  first: boolean; last: boolean;
  onMove: (by: -1 | 1) => void;
}) {
  const clamp = (v: string) => Math.max(1, Math.min(20, Number(v) || 1));
  /* Typed here, saved when the box is left: writing on every keystroke would
     store "1" on the way to "12", and redraw the room for each digit. */
  const [draft, setDraft] = useState<{ cols: string; rows: string } | null>(null);
  const cols = draft ? draft.cols : String(room.cols);
  const rows = draft ? draft.rows : String(room.rows);
  const commit = () => {
    if (!draft) return;
    setDraft(null);
    const c = clamp(draft.cols); const r = clamp(draft.rows);
    if (c !== room.cols || r !== room.rows) onResize(c, r);
  };
  return (
    <div className="w-full rounded-lg border-2 border-primary bg-primary/5 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-bold text-primary dark:text-primarySoft">{room.name}</span>
        {canEdit && !(first && last) && (
          <>
            <button type="button" onClick={() => onMove(-1)} disabled={first} aria-label={`Fill ${room.name} earlier`}
              className="text-[var(--sp-muted)] hover:text-primary disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onMove(1)} disabled={last} aria-label={`Fill ${room.name} later`}
              className="text-[var(--sp-muted)] hover:text-primary disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
          </>
        )}
        {canEdit && (
          <button type="button" onClick={onRemove} aria-label={`Remove ${room.name}`}
            className="text-[var(--sp-muted)] hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-[var(--sp-muted)]">
        <input type="number" min={1} max={20} value={cols} disabled={!canEdit}
          aria-label={`${room.name} desks wide`}
          onChange={(e) => setDraft({ cols: e.target.value, rows })}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-12 rounded border border-[var(--sp-line)] bg-[var(--sp-card)] px-1 py-0.5 text-center tabular-nums" />
        wide ×
        <input type="number" min={1} max={20} value={rows} disabled={!canEdit}
          aria-label={`${room.name} desks deep`}
          onChange={(e) => setDraft({ cols, rows: e.target.value })}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-12 rounded border border-[var(--sp-line)] bg-[var(--sp-card)] px-1 py-0.5 text-center tabular-nums" />
        deep · <strong className="text-[var(--sp-body)]">{roomCapacity(room)}</strong> desks
      </div>
      <label className="mt-1 flex items-center gap-1.5 text-[11px] text-[var(--sp-muted)]">
        Seat at most
        <input type="number" min={0} max={roomCapacity(room)} disabled={!canEdit}
          key={limit ?? 'all'} defaultValue={limit ?? ''} placeholder="all"
          aria-label={`At most how many candidates in ${room.name}`}
          onBlur={(e) => {
            const v = e.currentTarget.value.trim();
            const n = v === '' ? undefined : Math.max(0, Math.round(Number(v)) || 0);
            if (n !== limit) onLimit(n);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-14 rounded border border-[var(--sp-line)] bg-[var(--sp-card)] px-1 py-0.5 text-center tabular-nums" />
        here
      </label>
    </div>
  );
}

/* ── choosing a room: the exam rooms, then block by block ─────────── */

function RoomPicker({ schoolRooms, rooms, chosenIds, onChoose, onClose }: {
  schoolRooms: string[];
  rooms: ExamRoom[];
  chosenIds: string[];
  onChoose: (name: string) => void | Promise<void>;
  onClose: () => void;
}) {
  const groups = roomCatalogue({
    timetableRooms: schoolRooms,
    existing: rooms.map((r) => ({ id: r.id, name: r.name })),
  });
  return (
    <div className="mt-3 rounded-lg border border-[var(--sp-line)] bg-[var(--sp-paper)] p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sp-muted)]">
          The exam rooms first, then every room by block
        </p>
        <button type="button" onClick={onClose} aria-label="Close"
          className="text-[var(--sp-muted)] hover:text-ink"><X className="h-4 w-4" /></button>
      </div>
      <div className="max-h-72 space-y-3 overflow-y-auto">
        {groups.map((g) => (
          <div key={g.group}>
            <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[var(--sp-body)]">{g.group}</p>
            <div className="flex flex-wrap gap-1.5">
              {g.rooms.map((r) => {
                const inPlan = !!r.existingId && chosenIds.includes(r.existingId);
                return (
                  <button
                    key={r.name} type="button" disabled={inPlan}
                    onClick={() => { void onChoose(r.name); }}
                    className={`rounded-lg border px-2.5 py-1 text-sm transition-colors ${inPlan
                      ? 'cursor-default border-[var(--sp-line)] bg-[var(--sp-card)] text-[var(--sp-muted)]'
                      : 'border-[var(--sp-line)] bg-[var(--sp-card)] text-[var(--sp-body)] hover:border-primaryLight hover:text-primary'}`}
                  >
                    {r.name}{inPlan ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── the candidates, waiting to be seated ─────────────────────────── */

function CandidateList({ people, seats, picked, canEdit, papers, colours, onPick, nextId, onSendBack }: {
  /** Who a tap on an empty desk would seat, marked "next" on the list. */
  nextId: string;
  onSendBack: (studentId: string) => void;
  colours: Map<string, PaperColour>;
  people: SeatingCandidate[];
  seats: Record<string, Seat>;
  picked: string;
  canEdit: boolean;
  /** The papers sitting in this session, so each gets its own list. */
  papers: { code: string; count: number }[];
  onPick: (studentId: string) => void;
}) {
  const standing = people.filter((c) => !seats[c.studentId]);
  const seated = people.filter((c) => seats[c.studentId]);
  const several = papers.length > 1;
  /* A seated candidate dragged back onto the list leaves their desk. */
  const [over, setOver] = useState(false);
  const card = (c: SeatingCandidate, seat?: Seat) => {
    const colour = colourFor(colours, c.codes);
    return (
    <li key={c.studentId} className="relative">
      {seat && canEdit && (
        <button type="button" onClick={() => onSendBack(c.studentId)}
          aria-label={`Send ${c.candidateNumber} back to the list`} title="Back to the list"
          className="absolute right-1.5 top-1.5 z-10 rounded p-0.5 text-[var(--sp-muted)] hover:bg-[var(--sp-line)] hover:text-rose-600">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button" disabled={!canEdit}
        draggable={canEdit}
        onDragStart={(e) => { e.dataTransfer.setData('text/plain', c.studentId); onPick(c.studentId); }}
        onClick={() => onPick(c.studentId)}
        // The paper's colour on the card's edge, so a card and its desk match.
        style={colour ? { borderLeftColor: colour.ink, borderLeftWidth: 4 } : undefined}
        className={`w-full rounded-lg border px-2.5 py-1.5 text-left transition-colors ${
          c.studentId === picked
            ? 'border-primary bg-primary/10'
            : 'border-[var(--sp-line)] bg-[var(--sp-card)] hover:border-primaryLight'} ${seat ? 'opacity-60' : ''}`}
      >
        <span className="block text-sm font-bold tabular-nums text-ink dark:text-slate-100">
          {c.candidateNumber}
          {c.studentId === nextId && !picked && canEdit && (
            <span className="ml-1.5 rounded bg-primary/10 px-1 py-px align-middle text-[9px] font-bold uppercase tracking-wide text-primary dark:text-primarySoft">next</span>
          )}
        </span>
        <span className="block truncate text-[11px] text-[var(--sp-body)]">{c.candidateName}</span>
        <span className="block truncate text-[10px] text-[var(--sp-muted)]">
          {seat ? `${seat.roomName} desk ${seat.desk}` : c.codes.join(', ')}
        </span>
      </button>
    </li>
    );
  };

  return (
    <SoftCard pad="" className={`h-fit lg:sticky lg:top-4 ${over ? 'ring-2 ring-primary/50' : ''}`}>
      <div
        onDragOver={(e) => { if (canEdit && picked && seats[picked]) { e.preventDefault(); setOver(true); } }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setOver(false);
          const id = e.dataTransfer.getData('text/plain');
          if (id && seats[id]) onSendBack(id);
        }}
      >
      <PanelHeader
        icon={Users} title="Candidates"
        subtitle={standing.length ? `${standing.length} still to seat. Drag one onto a desk, or tap the desks in order` : 'Everybody is seated'}
      />
      <div className="max-h-[32rem] space-y-3 overflow-y-auto p-3">
        {/* One list per paper when a room sits more than one — the office
            works a paper at a time, even though the desks are shared. */}
        {several ? papers.map((p) => {
          const mine = standing.filter((c) => c.codes.includes(p.code));
          if (!mine.length) return null;
          return (
            <div key={p.code}>
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[var(--sp-body)]">
                {p.code} <span className="font-normal text-[var(--sp-muted)]">· {mine.length} to seat</span>
              </p>
              <ul className="space-y-1.5">{mine.map((c) => card(c))}</ul>
            </div>
          );
        }) : (!!standing.length && <ul className="space-y-1.5">{standing.map((c) => card(c))}</ul>)}
        {!!seated.length && (
          <details open={!standing.length}>
            <summary className="cursor-pointer text-xs font-semibold text-[var(--sp-muted)]">
              Seated ({seated.length})
            </summary>
            <ul className="mt-1.5 space-y-1.5">{seated.map((c) => card(c, seats[c.studentId]))}</ul>
          </details>
        )}
        {canEdit && !!seated.length && (
          <p className="text-[10px] text-[var(--sp-muted)]">Drag a desk back here to empty it.</p>
        )}
      </div>
      </div>
    </SoftCard>
  );
}

/* ── the room, seen from above ────────────────────────────────────── */

/**
 * A plan, not a grid. Walls, the board on its wall, the doors where they are,
 * and the desks spaced inside — see lib/examRoomLayout for the clearances.
 * The board and each door are DRAGGED onto a wall to move them; a wall is a
 * drop target along its whole length, so the drop is easy to hit.
 */
function RoomGrid({ room, people, seats, picked, canEdit, colours, onPick, onSeat, onRoomChange, blocked, blocking, onToggleBlock }: {
  /** Desks left empty in this session (`${roomId}|r2c3`), and whether a tap marks one. */
  blocked: string[];
  blocking: boolean;
  onToggleBlock: (row: number, col: number, occupant?: string) => void;
  colours: Map<string, PaperColour>;
  room: ExamRoom;
  people: SeatingCandidate[];
  seats: Record<string, Seat>;
  picked: string;
  canEdit: boolean;
  onPick: (studentId: string) => void;
  onSeat: (seat: Seat, studentId?: string) => void;
  onRoomChange: (room: ExamRoom) => void;
}) {
  const [over, setOver] = useState('');
  const [overWall, setOverWall] = useState<Wall | ''>('');

  const board: Wall = room.board ?? DEFAULT_FIXTURES.board;
  const doors: Door[] = room.doors ?? DEFAULT_FIXTURES.doors;
  const invig = room.invigilator ?? defaultInvigilator(board);
  const layout = roomLayout(room.cols, room.rows, { board, doors, invigilator: invig });

  const plan = roomPlan(room, people, seats);
  const occupantOf = new Map<string, SeatingCandidate | null>();
  const deskOf = new Map<string, { number: number; row: number; col: number }>();
  for (const row of plan) {
    for (const d of row) {
      occupantOf.set(deskKey(d.row, d.col), d.candidate);
      deskOf.set(deskKey(d.row, d.col), { number: d.number, row: d.row, col: d.col });
    }
  }
  const seated = plan.flat().filter((d) => d.candidate).length;
  const usable = desksOf(room).length;
  const blockedHere = new Set(blocked.filter((k) => k.startsWith(`${room.id}|`)).map((k) => k.slice(room.id.length + 1)));
  const leftEmpty = blockedHere.size;

  /* What is being dragged onto a wall: the board, the invigilator's desk, or door number n. */
  const [dragging, setDragging] = useState<'board' | 'invig' | number | null>(null);
  const dropOnWall = (wall: Wall) => {
    if (dragging === null) return;
    if (dragging === 'board') onRoomChange({ ...room, board: wall, doors });
    else if (dragging === 'invig') onRoomChange({ ...room, board, doors, invigilator: { wall, at: invig.at } });
    else {
      const next = doors.map((d, i) => (i === dragging ? { ...d, wall } : d));
      onRoomChange({ ...room, board, doors: next });
    }
    setDragging(null);
    setOverWall('');
  };

  const nudgeDoor = (i: number, delta: number) => {
    const next = doors.map((d, n) => (n === i ? { ...d, at: Math.min(1, Math.max(0, d.at + delta)) } : d));
    onRoomChange({ ...room, board, doors: next });
  };

  const pct = (n: number) => `${n}%`;

  return (
    <SoftCard pad="">
      <PanelHeader
        icon={Grid3x3} title={room.name}
        subtitle={`${seated} of ${usable} desks · ${room.cols} wide × ${room.rows} deep${
          leftEmpty ? ` · ${leftEmpty} left empty this session` : ''}`}
      />

      {canEdit && (
        <div className="flex flex-wrap items-center gap-3 border-b border-[var(--sp-line)] px-4 py-2 text-xs text-[var(--sp-muted)]">
          <span className="font-semibold">Board</span>
          <div className="inline-flex overflow-hidden rounded border border-[var(--sp-line)]">
            {WALLS.map((w) => (
              <button
                key={w} type="button" onClick={() => onRoomChange({ ...room, board: w, doors })}
                aria-pressed={board === w}
                className={`px-2 py-1 ${board === w ? 'bg-primary text-white' : 'hover:bg-[var(--sp-paper)]'}`}
              >{WALL_LABEL[w]}</button>
            ))}
          </div>
          <button type="button" className={`${btnGhost} px-2.5 py-1 text-xs`}
            onClick={() => onRoomChange({ ...room, board, doors: [...doors, { wall: 'right', at: 0.5 }] })}>
            <Plus className="h-3.5 w-3.5" /> Add a door
          </button>
          <span>Drag the board, the invigilator's desk or a door onto a wall to move it. Desks are spaced automatically.</span>
        </div>
      )}

      <div className="p-4">
        {colours.size > 1 && (
          <div className="mb-2 flex flex-wrap items-center gap-3 text-[11px]">
            {[...colours.values()].map((colour) => {
              const n = plan.flat().filter((d) => d.candidate?.codes.includes(colour.code)).length;
              if (!n) return null;
              return (
                <span key={colour.code} className="inline-flex items-center gap-1.5 font-semibold text-[var(--sp-body)]"
                  >
                  <span className="inline-block h-3 w-3 rounded-sm border"
                    style={{ backgroundColor: colour.fill, borderColor: colour.ink }} />
                  {colour.code} <span className="font-normal text-[var(--sp-muted)]">({n})</span>
                </span>
              );
            })}
          </div>
        )}
        <div
          className="relative mx-auto w-full max-w-3xl rounded-lg border-4 border-[var(--sp-line-2)] bg-[var(--sp-paper)]"
          style={{ aspectRatio: `${Math.max(3, room.cols)} / ${Math.max(3, room.rows)}` }}
        >
          {/* The walls, as drop targets along their whole length. */}
          {canEdit && WALLS.map((w) => (
            <div
              key={w}
              onDragOver={(e) => { if (dragging !== null) { e.preventDefault(); setOverWall(w); } }}
              onDragLeave={() => setOverWall((x) => (x === w ? '' : x))}
              onDrop={(e) => { e.preventDefault(); dropOnWall(w); }}
              aria-hidden
              className={`absolute ${overWall === w ? 'bg-primary/20' : ''} ${
                w === 'front' ? 'left-0 right-0 top-0 h-[9%]'
                  : w === 'back' ? 'bottom-0 left-0 right-0 h-[9%]'
                    : w === 'left' ? 'bottom-0 left-0 top-0 w-[9%]' : 'bottom-0 right-0 top-0 w-[9%]'}`}
            />
          ))}

          {/* The board. */}
          <div
            draggable={canEdit}
            onDragStart={() => setDragging('board')}
            onDragEnd={() => { setDragging(null); setOverWall(''); }}
            title="The board — drag it to another wall"
            className="absolute flex items-center justify-center rounded-sm bg-[#2f6ca9] text-[9px] font-bold uppercase tracking-widest text-white shadow"
            style={{ left: pct(layout.board.x), top: pct(layout.board.y), width: pct(layout.board.w), height: pct(layout.board.h), cursor: canEdit ? 'grab' : 'default' }}
          >
            <span className={layout.boardWall === 'left' || layout.boardWall === 'right' ? 'rotate-90' : ''}>board</span>
          </div>

          {/* The invigilator's desk: Cambridge's plan must show where it is. */}
          <div
            draggable={canEdit}
            onDragStart={() => setDragging('invig')}
            onDragEnd={() => { setDragging(null); setOverWall(''); }}
            title="The invigilator's desk — drag it to another wall"
            className="group absolute flex items-center justify-center rounded-sm border border-emerald-600 bg-emerald-50 dark:border-emerald-500 dark:bg-emerald-950/60"
            style={{ left: pct(layout.invigilator.x), top: pct(layout.invigilator.y), width: pct(layout.invigilator.w), height: pct(layout.invigilator.h), cursor: canEdit ? 'grab' : 'default' }}
          >
            <span className={`text-[7px] font-bold uppercase text-emerald-800 dark:text-emerald-300 ${layout.invigilator.wall === 'left' || layout.invigilator.wall === 'right' ? 'rotate-90' : ''}`}>invigilator</span>
            {canEdit && (
              <span className="absolute -top-5 hidden gap-0.5 group-hover:flex">
                <button type="button" aria-label="Move the invigilator's desk along the wall"
                  onClick={() => onRoomChange({ ...room, board, doors, invigilator: { ...invig, at: Math.max(0, invig.at - 0.1) } })}
                  className="rounded bg-[var(--sp-card)] px-1 text-[10px] shadow ring-1 ring-[var(--sp-line)]">◂</button>
                <button type="button" aria-label="Move the invigilator's desk the other way"
                  onClick={() => onRoomChange({ ...room, board, doors, invigilator: { ...invig, at: Math.min(1, invig.at + 0.1) } })}
                  className="rounded bg-[var(--sp-card)] px-1 text-[10px] shadow ring-1 ring-[var(--sp-line)]">▸</button>
              </span>
            )}
          </div>

          {/* The doors. */}
          {layout.doors.map((d, i) => (
            <div
              key={i}
              draggable={canEdit}
              onDragStart={() => setDragging(i)}
              onDragEnd={() => { setDragging(null); setOverWall(''); }}
              title={`Door on the ${WALL_LABEL[d.wall].toLowerCase()} wall — drag it to another wall`}
              className="group absolute flex items-center justify-center bg-[var(--sp-card)] ring-2 ring-[#d9a441]"
              style={{ left: pct(d.x), top: pct(d.y), width: pct(d.w), height: pct(d.h), cursor: canEdit ? 'grab' : 'default' }}
            >
              <span className="text-[8px] font-bold uppercase text-[#a9791f]">door</span>
              {canEdit && (
                <span className="absolute -top-5 hidden gap-0.5 group-hover:flex">
                  <button type="button" aria-label="Move the door along the wall"
                    onClick={() => nudgeDoor(i, -0.15)}
                    className="rounded bg-[var(--sp-card)] px-1 text-[10px] shadow ring-1 ring-[var(--sp-line)]">◂</button>
                  <button type="button" aria-label="Move the door the other way"
                    onClick={() => nudgeDoor(i, 0.15)}
                    className="rounded bg-[var(--sp-card)] px-1 text-[10px] shadow ring-1 ring-[var(--sp-line)]">▸</button>
                  <button type="button" aria-label="Remove this door"
                    onClick={() => onRoomChange({ ...room, board, doors: doors.filter((_, n) => n !== i) })}
                    className="rounded bg-[var(--sp-card)] px-1 text-[10px] text-rose-600 shadow ring-1 ring-[var(--sp-line)]">×</button>
                </span>
              )}
            </div>
          ))}

          {/* The desks. */}
          {layout.desks.map((box) => {
            const key = deskKey(box.row, box.col);
            const info = deskOf.get(key);
            const c = occupantOf.get(key) ?? null;
            const isDead = !info;
            const isBlocked = !isDead && blockedHere.has(key);
            const chosen = c && c.studentId === picked;
            const seat = info
              ? { roomId: room.id, roomName: room.name, desk: info.number, row: box.row, col: box.col }
              : null;
            return (
              <button
                key={key}
                type="button"
                disabled={!canEdit || isDead}
                draggable={canEdit && !!c && !blocking}
                onDragStart={(e) => { if (c) { e.dataTransfer.setData('text/plain', c.studentId); onPick(c.studentId); } }}
                onDragOver={(e) => { if (canEdit && seat && !isBlocked && !blocking) { e.preventDefault(); setOver(key); } }}
                onDragLeave={() => setOver((k) => (k === key ? '' : k))}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver('');
                  if (!seat || isBlocked) return;
                  onSeat(seat, e.dataTransfer.getData('text/plain'));
                }}
                onClick={() => {
                  if (!seat) return;
                  if (blocking) { onToggleBlock(box.row, box.col, c?.studentId); return; }
                  if (isBlocked) return;
                  if (chosen) { onPick(''); return; }
                  if (c && !picked) { onPick(c.studentId); return; }
                  // An empty desk with nobody picked seats the next on the list.
                  onSeat(seat);
                }}
                title={isDead ? 'Not usable' : isBlocked ? `Desk ${info?.number}, left empty this session` : c ? `${c.candidateNumber} ${c.candidateName} — ${c.codes.join(', ')}` : `Desk ${info?.number}, empty`}
                style={{
                  left: pct(box.x), top: pct(box.y), width: pct(box.w), height: pct(box.h),
                  ...(c && colourFor(colours, c.codes) && over !== key && !chosen
                    ? { backgroundColor: colourFor(colours, c.codes)!.fill, borderColor: colourFor(colours, c.codes)!.ink }
                    : {}),
                }}
                className={`absolute flex flex-col items-center justify-center overflow-hidden rounded border text-center leading-none transition-colors ${
                  isDead ? 'border-dashed border-rose-300 bg-rose-50/60 text-rose-400 dark:bg-rose-950/30'
                    : isBlocked ? 'border-dashed border-[var(--sp-line-2)] bg-[repeating-linear-gradient(45deg,transparent_0_4px,var(--sp-line)_4px_5px)] text-[var(--sp-muted)]'
                    : over === key ? 'border-primary bg-primary/25 ring-2 ring-primary/50'
                      : chosen ? 'border-primary bg-primary/15'
                        : c ? 'border-[var(--sp-line-2)] bg-[var(--sp-card)] hover:border-primaryLight'
                          : 'border-dashed border-[var(--sp-line)] bg-[var(--sp-card)]/60 hover:border-primaryLight'}`}
              >
                {isDead ? (
                  <span className="text-[9px]">×</span>
                ) : isBlocked ? (
                  <span className="text-[8px] font-semibold uppercase">empty</span>
                ) : c ? (
                  <>
                    <span
                      className="w-full truncate px-0.5 text-[11px] font-bold tabular-nums text-ink dark:text-slate-100"
                      style={colourFor(colours, c.codes) ? { color: colourFor(colours, c.codes)!.ink } : undefined}
                    >
                      {c.candidateNumber}
                    </span>
                    <span className="w-full truncate px-0.5 text-[8px] text-[var(--sp-muted)]">{info?.number}</span>
                  </>
                ) : (
                  <span className="text-[8px] text-[var(--sp-muted)]">{info?.number}</span>
                )}
              </button>
            );
          })}
        </div>

        <p className="mt-2 text-center text-[11px] text-[var(--sp-muted)]">
          Row 1 is nearest the board. Desks are numbered in reading order from row 1.
          {doorBlocked(layout) && (
            <span className="ml-1 font-semibold text-amber-700 dark:text-amber-400">
              A desk is in the doorway — move the door or take a desk out.
            </span>
          )}
        </p>
      </div>
    </SoftCard>
  );
}
